import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import articles from '../content/blog.json';
import './WebsiteAnalytics.css';

const measurementId = 'G-WQSWQ7LF53';
const consentKey = 'standlogic-analytics-consent';
type Consent = 'accepted' | 'declined' | null;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    'ga-disable-G-WQSWQ7LF53'?: boolean;
  }
}

export function isAnalyticsWebsite() {
  return !Capacitor.isNativePlatform() &&
    window.location.protocol === 'https:' &&
    ['standlogic.app', 'www.standlogic.app'].includes(window.location.hostname);
}

function readConsent(): Consent {
  if (!isAnalyticsWebsite()) return null;
  try {
    const value = localStorage.getItem(consentKey);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch (error) {
    console.error('Unable to read analytics preference:', error);
    return null;
  }
}

function WebsiteAnalytics() {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState<Consent>(readConsent);
  const [editing, setEditing] = useState(false);
  const [storageError, setStorageError] = useState('');
  const previousPage = useRef('');
  const enabled = isAnalyticsWebsite();

  useEffect(() => {
    if (!enabled || consent !== 'accepted') return;
    window['ga-disable-G-WQSWQ7LF53'] = false;
    // Never send OAuth URLs or arbitrary paths that could contain personal data.
    const normalizedPath = pathname.toLowerCase().replace(/\/+$/, '') || '/';
    if (normalizedPath === '/auth/callback') return;
    const knownPaths = [
      '/', '/blog', '/signin', '/optin', '/setup', '/dashboard', '/outlook',
      '/daily', '/monthly', '/profile', '/admin', '/support', '/privacy',
      '/terms', '/disclaimer', '/delete-account',
      ...articles.map((article) => `/blog/${article.slug}`),
    ];
    const pagePath = knownPaths.includes(normalizedPath) ? normalizedPath : '/not-found';
    const pageUrl = `${window.location.origin}${pagePath}`;
    if (previousPage.current === pageUrl) return;

    if (!document.getElementById('standlogic-google-tag')) {
      window.dataLayer = window.dataLayer || [];
      const dataLayer = window.dataLayer;
      window.gtag = function () { dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
        page_location: pageUrl,
        page_referrer: '',
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      const script = document.createElement('script');
      script.id = 'standlogic-google-tag';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.onerror = () => console.error('Unable to load Google Analytics.');
      document.head.appendChild(script);
    }
    window.gtag?.('event', 'page_view', {
      page_location: pageUrl,
      page_referrer: previousPage.current,
      page_title: pagePath,
    });
    previousPage.current = pageUrl;
  }, [consent, enabled, pathname]);

  function saveConsent(value: Exclude<Consent, null>) {
    window['ga-disable-G-WQSWQ7LF53'] = value !== 'accepted';
    if (value === 'declined') previousPage.current = '';
    setStorageError('');
    try {
      localStorage.setItem(consentKey, value);
    } catch (error) {
      console.error('Unable to save analytics preference:', error);
      setStorageError('Your choice applies to this page session, but could not be saved for future visits.');
    }
    setConsent(value);
    setEditing(false);
  }

  if (!enabled) return null;
  return (
    <footer className="analytics-preferences" aria-label="Analytics privacy preferences">
      {consent === null || editing ? (
        <div className="analytics-consent">
          <h2>Optional analytics cookies</h2>
          <p>
            With your permission, we use Google Analytics to understand website visits.
            It uses cookies and sends usage information to Google. You can decline and
            still use the site, or change your choice at the bottom of any page. <Link to="/privacy">Privacy policy</Link>
          </p>
          <div className="analytics-consent-actions">
            <button type="button" className="btn btn-secondary" onClick={() => saveConsent('declined')}>Decline analytics</button>
            <button type="button" className="btn btn-secondary" onClick={() => saveConsent('accepted')}>Accept analytics</button>
          </div>
        </div>
      ) : (
        <button type="button" className="analytics-preferences-link" onClick={() => setEditing(true)}>Analytics preferences</button>
      )}
      {storageError && <p role="alert">{storageError}</p>}
    </footer>
  );
}

export default WebsiteAnalytics;
